/**
=========================================================
* Soft UI Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { createContext, forwardRef, useContext } from "react";

// Core
import PropTypes from "prop-types";

// Components
import Box from "components/Atoms/Box";
import Button from "components/Atoms/Button";

// Styles
import styles from "components/Atoms/Pagination/styles";

// The Pagination main context
const Context = createContext();

const SuiPagination = forwardRef(
  ({ item, variant, color, size, active, children, ...rest }, ref) => {
    const context = useContext(Context);
    const paginationSize = context ? context.size : null;
    const classes = styles({ paginationSize, variant, active });

    return (
      <Context.Provider value={{ variant, color, size }}>
        {item ? (
          <Button
            {...rest}
            ref={ref}
            variant={active ? context.variant : "outlined"}
            color={active ? context.color : "secondary"}
            iconOnly
            circular
            customClass={classes.suiPagination}
          >
            {children}
          </Button>
        ) : (
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={1} // adds spacing between pagination items
            customClass="no-list-style"
          >
            {children}
          </Box>
        )}
      </Context.Provider>
    );
  }
);

// Setting default values for the props of SuiPagination
SuiPagination.defaultProps = {
  item: false,
  variant: "gradient",
  color: "info",
  size: "medium",
  active: false,
};

// Typechecking props for the SuiPagination
SuiPagination.propTypes = {
  item: PropTypes.bool,
  variant: PropTypes.oneOf(["gradient", "contained"]),
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  active: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

export default SuiPagination;
